
import { cn } from "@/lib/utils";
import { Shield, ShieldAlert, ShieldCheck } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface IntegrityDisplayProps {
  score: number | null;
  className?: string;
}

export function IntegrityDisplay({ score = null, className }: IntegrityDisplayProps) {
  // Default to 100 if no score provided
  const integrityScore = score ?? 100;
  
  const getIcon = () => {
    if (integrityScore >= 90) {
      return <ShieldCheck className="h-4 w-4 text-green-500" />;
    } else if (integrityScore >= 70) {
      return <Shield className="h-4 w-4 text-amber-500" />;
    } else {
      return <ShieldAlert className="h-4 w-4 text-red-500" />;
    }
  };

  const getDescription = () => {
    if (integrityScore >= 90) {
      return "High Integrity - No suspicious activities detected";
    } else if (integrityScore >= 70) {
      return "Medium Integrity - Some suspicious activities detected";
    } else {
      return "Low Integrity - Multiple integrity violations detected";
    }
  };

  const getColor = () => {
    if (integrityScore >= 90) {
      return "text-green-600 dark:text-green-500";
    } else if (integrityScore >= 70) {
      return "text-amber-600 dark:text-amber-500";
    } else {
      return "text-red-600 dark:text-red-500";
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn("flex items-center gap-1.5", className)}>
            {getIcon()}
            <span className={cn("font-medium", getColor())}>
              {Math.round(integrityScore)}%
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="max-w-xs text-sm">{getDescription()}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
